import { useMemo } from 'react';

import {
  ActivityConfig,
  AvailableInstance,
  EMappedIntegrationTypes,
  IntegrationActionDB,
  IntegrationsMappedOptions,
  IntegrationsMappedDB,
} from '@/types/integrations.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import RegistrationIntegrationForm, { SelectOption } from './registration-integration-form';
import ActivityIntegrationForm from './activity-integration-form';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import CustomReactSelect from '@ui/select/react-select/custom-react-select';
import { reactSelectAdminStyles } from '@ui/select/react-select/react-select-styles';
import { useAppSelector } from '@/store/hooks';
import { selectSolutionsMapped } from '@/store/slices/channel-settings/selectors';

interface OnChangePayload {
  overrides?: IntegrationsMappedOptions;
  valid?: boolean;
}

interface EventIntegrationOverridesFormProps {
  instance: AvailableInstance;
  overrides?: IntegrationsMappedOptions;
  actions: IntegrationActionDB[];
  onChange: (payload: OnChangePayload) => void;
  handleInstanceChange?: React.Dispatch<React.SetStateAction<IntegrationsMappedDB>>;
  solutionTitle?: string;
  channelIntegration?: boolean;
  integrationName?: string;
}

function EventIntegrationOverridesForm({
  instance,
  overrides,
  actions,
  onChange,
  handleInstanceChange,
  solutionTitle,
  channelIntegration = false,
  integrationName,
}: EventIntegrationOverridesFormProps) {
  const solutionsMapped = useAppSelector(selectSolutionsMapped);

  function handleActivityMappingChange({
    activityMapping,
    staticMapping,
    valid,
  }: {
    activityMapping?: Record<string, ActivityConfig>;
    staticMapping?: Record<string, string>;
    valid?: boolean;
  }): void {
    if (!overrides) return;

    const updatedOverrides = { ...overrides };

    if (activityMapping && updatedOverrides?.outbound_activity_configuration?.enabled_actions) {
      updatedOverrides.outbound_activity_configuration.enabled_actions = activityMapping;
    }

    if (staticMapping) {
      updatedOverrides.static_mapping = staticMapping;
    }

    const onChangePayload: OnChangePayload = {
      overrides: updatedOverrides,
    };

    if (valid !== undefined) {
      onChangePayload.valid = valid;
    }

    onChange(onChangePayload);
  }

  function handleRegistrationMappingChange({
    registrationMapping,
    staticMapping,
    queryMapping,
    optInMapping,
    actionTitleKeyname,
    valid,
    convertNonAsciiCharacters,
  }: {
    registrationMapping?: Record<string, string>;
    staticMapping?: Record<string, string>;
    queryMapping?: Record<string, string>;
    optInMapping?: string;
    actionTitleKeyname?: string;
    valid?: boolean;
    convertNonAsciiCharacters?: boolean;
  }): void {
    if (!overrides) return;

    const updatedOverrides = { ...overrides };

    if (registrationMapping) {
      updatedOverrides.registration_mapping = registrationMapping;
    }

    if (staticMapping) {
      updatedOverrides.static_mapping = staticMapping;
    }

    if (queryMapping) {
      updatedOverrides.query_mapping = queryMapping;
    }

    if (optInMapping) {
      updatedOverrides.opt_in_mapping = optInMapping;
    }

    if (actionTitleKeyname) {
      updatedOverrides.action_title_keyname = actionTitleKeyname;
    }

    if (convertNonAsciiCharacters !== undefined) {
      updatedOverrides.convert_non_ascii_characters = convertNonAsciiCharacters;
    }

    const onChangePayload: OnChangePayload = {
      overrides: updatedOverrides,
    };

    if (valid !== undefined) {
      onChangePayload.valid = valid;
    }

    onChange(onChangePayload);
  }

  const endpointOptions = actions.map((action) => ({
    label: action.action_label,
    value: action.id,
  }));

  const selectedAction = actions.find((action) => action.action_label === overrides?.action?.action_label);
  const selectedActionValue = selectedAction ? { label: selectedAction.action_label, value: selectedAction.id } : null;

  const selectEndpoint = (option: unknown) => {
    const value = option as SelectOption;
    if (!overrides) return;
    const updatedOverrides = { ...overrides };
    const updatedAction = actions.find((action) => action.id === value.value);

    if (updatedAction) {
      updatedOverrides.action = updatedAction;
    }

    const onChangePayload: OnChangePayload = {
      overrides: updatedOverrides,
    };

    onChange(onChangePayload);
  };

  const integrationTypeOptionsMapped = useMemo(() => {
    if (integrationName && solutionsMapped?.[integrationName]) {
      return Object.entries(solutionsMapped[integrationName])?.map(([key]) => {
        switch (key) {
          case EMappedIntegrationTypes.registration: {
            return {
              label: 'Registration',
              value: key,
            };
          }
          case EMappedIntegrationTypes.activity: {
            return {
              label: 'Activity tracking',
              value: key,
            };
          }
          // Default case assumes a custom solution, which is keyed on solution uuid rather than EMappedIntegrationTypes
          // For this case, we use the solution description from tray to render the label, rather than the integration type
          default: {
            return {
              // @ts-expect-error expect any
              label: solutionsMapped[integrationName][key].description,
              // @ts-expect-error expect any
              value: solutionsMapped[integrationName][key].id,
            };
          }
        }
      });
    } else return [];
  }, [solutionsMapped, integrationName]);

  const getIntegrationMappedType = (key: string) => {
    const registration = { type: EMappedIntegrationTypes.registration, value: key };
    if (!solutionsMapped || !integrationName) {
      return registration;
    }
    if (key === EMappedIntegrationTypes.registration) return registration;
    if (key === EMappedIntegrationTypes.inbound) return { type: EMappedIntegrationTypes.inbound, value: key };
    if (key === EMappedIntegrationTypes.activity) return { type: EMappedIntegrationTypes.activity, value: key };
    // @ts-expect-error expect any
    if (integrationName && solutionsMapped[integrationName]?.[key])
      // @ts-expect-error expect any
      return { type: solutionsMapped[integrationName][key].type ?? EMappedIntegrationTypes.registration, value: key };
    return registration;
  };

  const handleIntegrationTypeChange = (option: unknown) => {
    const { value: _value } = option as SelectOption;
    const value = _value as string;

    const mappedTyped = getIntegrationMappedType(value);

    if (integrationName && solutionsMapped && handleInstanceChange) {
      switch (mappedTyped.value) {
        case EMappedIntegrationTypes.activity: {
          handleInstanceChange({
            ...instance,
            type: EMappedIntegrationTypes.activity,
            // @ts-expect-error expect any
            solution_uuid: solutionsMapped[integrationName][EMappedIntegrationTypes.activity].id,
          });
          return;
        }
        default: {
          // @ts-expect-error expect any
          if (solutionsMapped[integrationName][value]) {
            handleInstanceChange({
              ...instance,
              type: mappedTyped.type,
              // @ts-expect-error expect any
              solution_uuid: solutionsMapped[integrationName][value].id,
            });
          } else {
            handleInstanceChange({
              ...instance,
              type: EMappedIntegrationTypes.registration,
              // @ts-expect-error expect any
              solution_uuid: solutionsMapped[integrationName][EMappedIntegrationTypes.registration].id,
            });
          }
          return;
        }
      }
    }
  };

  return (
    <div>
      <div className="channel-defaults">
        <div className="channel-defaults-header grid-row">
          <div className="defaults-name">
            <InputWithOverlay
              label={'Integration name*'}
              inputProps={{
                defaultValue: instance.name || '',
                disabled: !channelIntegration,
                onChange: (e) => handleInstanceChange?.({ ...instance, name: e.target.value }),
              }}
            />
          </div>
          <div className="defaults-type">
            <OptionalComponent display={!channelIntegration}>
              <InputWithOverlay
                label={'Integration type*'}
                inputProps={{ disabled: true, placeholder: instance?.type }}
              />
            </OptionalComponent>
            <OptionalComponent display={channelIntegration && !!integrationName}>
              <CustomReactSelect
                label="Integration type*"
                options={integrationTypeOptionsMapped}
                onChange={handleIntegrationTypeChange}
                value={integrationTypeOptionsMapped.find((option) => option.value === instance.type)}
                styles={reactSelectAdminStyles({
                  container: () => ({ height: '46px', width: '95%' }),
                  control: () => ({ paddingLeft: '0', height: '100%', maxHeight: '100%' }),
                  menuList: () => ({ backgroundColor: 'var(--gray-800)', maxHeight: '300px' }),
                  option: () => ({
                    height: '36px',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }),
                })}
                closeMenuOnSelect
              />
            </OptionalComponent>
          </div>
        </div>
        <OptionalComponent display={instance.type === EMappedIntegrationTypes.registration && !!actions.length}>
          <CustomReactSelect
            label={'API Endpoint*'}
            options={endpointOptions}
            onChange={selectEndpoint}
            value={selectedActionValue}
            placeholder="Select receiver endpoint"
            styles={reactSelectAdminStyles({
              container: () => ({ height: '46px', width: '95%' }),
              control: () => ({ paddingLeft: '0', height: '100%', maxHeight: '100%' }),
              menuList: () => ({ backgroundColor: 'var(--gray-800)', maxHeight: '300px' }),
              option: () => ({ height: '36px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }),
            })}
            closeMenuOnSelect
          />
        </OptionalComponent>
      </div>
      <OptionalComponent display={instance.type === EMappedIntegrationTypes.registration}>
        <RegistrationIntegrationForm
          instance={instance}
          values={overrides}
          onChange={handleRegistrationMappingChange}
          solutionTitle={solutionTitle}
          channelIntegration={channelIntegration}
        />
      </OptionalComponent>
      <OptionalComponent display={instance.type === EMappedIntegrationTypes.activity}>
        <ActivityIntegrationForm
          onChange={handleActivityMappingChange}
          edit
          instance={instance}
          values={overrides}
          isOverrideMapping={!channelIntegration}
        />
      </OptionalComponent>
    </div>
  );
}

export default EventIntegrationOverridesForm;
